import { signal } from '@angular/core';

import { Clock } from '../runtime/clock/clock';
import { SlideController } from '../runtime/playback/interfaces';
import { RuntimeSlideModel } from '../runtime/slides/models';

/** One slide's answer to "am I finished?" — the duration plus whatever the rendered layout adds. */
export class PresentationController implements SlideController {
  private readonly abort = new AbortController();
  private readonly pending = new Set<symbol>();
  private readonly media = Symbol('media');
  private resolveFinished!: () => void;
  private sealed = false;
  private completed = false;

  public readonly finished = new Promise<void>(resolve => { this.resolveFinished = resolve; });
  public readonly image = signal('');
  public readonly signal = this.abort.signal;
  /** Set when the slide could not actually be shown, so an interrupt may be offered again. */
  public failed = false;


  public constructor(public readonly slide: RuntimeSlideModel, private readonly clock: Clock) {
    const duration = slide.duration.kind === 'media' ? 0 : slide.duration.milliseconds;
    this.wait(clock.after(duration, this.signal));
    // A fullscreen split slide shows the video's thumbnail without ever playing it, so it owns no media wait.
    if (slide.layout === 'video' || (slide.content.showVideo && !slide.hasFullscreenContent)) {
      this.pending.add(this.media);
    }
  }

  public present(signal: AbortSignal): Promise<void> {
    if (signal.aborted) {
      this.destroy();

      return this.finished;
    }

    signal.addEventListener('abort', () => this.destroy(), { once: true });

    return this.finished;
  }

  public wait(work: Promise<unknown>): void {
    const release = this.hold();
    void work.catch((): void => undefined).finally(release);
  }

  /** Reserves a slot synchronously, so a child that renders later cannot be raced by the duration timer. */
  public hold(): () => void {
    const token = Symbol('presentation');
    this.pending.add(token);

    return (): void => {
      this.pending.delete(token);
      this.checkFinished();
    };
  }

  public ready(): void {
    this.sealed = true;
    this.checkFinished();
  }

  public fail(): void {
    this.failed = true;
  }

  public mediaEnded(): void {
    this.pending.delete(this.media);
    this.checkFinished();
  }

  public destroy(): void {
    this.abort.abort();
    this.pending.clear();
    this.completed = true;
    this.resolveFinished();
  }

  public dispose(): void {
    this.destroy();
  }

  public async carousel(images: readonly string[], seconds = 5): Promise<void> {
    for (const image of images) {
      if (this.signal.aborted) {
        return;
      }

      this.image.set(image);
      await this.clock.after(Math.max(1, seconds) * 1000, this.signal);
    }
  }

  private checkFinished(): void {
    if (this.sealed && !this.completed && this.pending.size === 0 && !this.signal.aborted) {
      this.completed = true;
      this.resolveFinished();
    }
  }
}
